import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { PAGE_SIZE, PageSizeParams } from 'athena/components/mentoring-program/constants';
import axios from 'axios';
import { MentoringProgramGoalItem } from 'redux/schemas/models/mentoring-program-goal-items';

type MentorshipProgramParams = {
  mentorshipProgramId: number;
};

export type MentoringProgramGoalResponse = {
  enrollmentId: number;
  connectionId?: number;
  goalItem: MentoringProgramGoalItem;
};

export type CreateMentorshipProgramGoalItemParams = MentorshipProgramParams &
Pick<MentoringProgramGoalItem, 'description' | 'status'> & {
  enrollmentId: number;
  assignedTo?: number;
  connectionId: number;
};

export const createMentorshipProgramGoalItem = createAsyncThunk<
MentoringProgramGoalResponse,
CreateMentorshipProgramGoalItemParams
>(
  'CREATE_MENTORSHIP_PROGRAM_GOAL_ITEM',
  async ({ mentorshipProgramId, description, status, enrollmentId, assignedTo, connectionId }) => {
    const goal = {
      description,
      status,
      assignedTo,
      mentorshipProgramConnectionId: connectionId,
    };
    const response = await axios.post(
      `/mentorship_programs/${mentorshipProgramId}/mentorship_program_enrollment_goals`,
      {
        goal,
      },
    );
    const goalItem = response.data.result as MentoringProgramGoalItem;
    return { enrollmentId, connectionId, goalItem };
  },
);

export type GetMentorshipProgramGoalItemsParams = MentorshipProgramParams &
PageSizeParams & {
  page: number;
  enrollmentId?: number;
  connectionId: number;
  statusSort?: 'asc' | 'desc';
};

export type GetMentorshipProgramGoalItemsResponse = {
  mentorshipProgramEnrollmentGoals: MentoringProgramGoalItem[];
  totalCount: number;
};
export const getMentorshipProgramGoalItems = createAsyncThunk<
MentoringProgramGoalItem[],
GetMentorshipProgramGoalItemsParams
>(
  'GET_MENTORSHIP_PROGRAM_GOAL_ITEMS',
  async ({
    mentorshipProgramId,
    page,
    pageSize,
    enrollmentId,
    connectionId,
    statusSort,
  }, thunkAPI) => {
    const params = {
      page,
      page_size: pageSize,
      mentorship_program_connection_id: connectionId,
      assigned_to: enrollmentId || undefined,
      status_asc: statusSort === 'asc' || undefined,
      status_desc: statusSort === 'desc' || undefined,
    };
    const response = await axios.get(
      `/mentorship_programs/${mentorshipProgramId}/mentorship_program_enrollment_goals`,
      {
        params,
      },
    );
    const { mentorshipProgramEnrollmentGoals, totalCount } = response.data.result as GetMentorshipProgramGoalItemsResponse;

    const stateKey = enrollmentId ?? connectionId;
    if (stateKey !== null && stateKey !== undefined) {
      thunkAPI.dispatch(setMentorshipProgramGoalsTotalCount({ enrollmentId: stateKey, totalCount }));
    }

    return mentorshipProgramEnrollmentGoals;
  },
);

export type UpdateMentorshipProgramGoalItemParams = MentorshipProgramParams &
Partial<Pick<MentoringProgramGoalItem, 'description' | 'status'>> & {
  id: MentoringProgramGoalItem['id'];
  enrollmentId: number;
  connectionId: number;
  assignedTo?: number;
};

export const updateMentorshipProgramGoalItem = createAsyncThunk<
MentoringProgramGoalResponse,
UpdateMentorshipProgramGoalItemParams
>(
  'UPDATE_MENTORSHIP_PROGRAM_GOAL_ITEM',
  async ({ mentorshipProgramId, id, description, status, enrollmentId, connectionId, assignedTo }, thunkAPI) => {
    const goal = {
      description: description !== undefined ? description : undefined,
      status: status !== undefined ? status : undefined,
      assignedTo: assignedTo !== undefined ? assignedTo : undefined,
      mentorshipProgramConnectionId: connectionId,
    };
    const response = await axios.put(
      `/mentorship_programs/${mentorshipProgramId}/mentorship_program_enrollment_goals/${id}.json`,
      {
        goal,
      },
    );
    const goalItem = response.data.result as MentoringProgramGoalItem;
    if (assignedTo !== undefined) {
      thunkAPI.dispatch(getMentorshipProgramGoalItems({
        mentorshipProgramId,
        enrollmentId,
        connectionId,
        page: 1,
        pageSize: PAGE_SIZE,
      }));
      if (goalItem.assignedTo?.id) {
        thunkAPI.dispatch(getMentorshipProgramGoalItems({
          mentorshipProgramId,
          enrollmentId: goalItem.assignedTo.id,
          connectionId,
          page: 1,
          pageSize: PAGE_SIZE,
        }));
      }
    }
    return { enrollmentId, goalItem };
  },
);

type DeleteMentorshipProgramGoalItemParams = MentorshipProgramParams & {
  id: MentoringProgramGoalItem['id'];
  enrollmentId: number;
  connectionId: number;
};

export const deleteMentorshipProgramGoalItem = createAsyncThunk<
boolean,
DeleteMentorshipProgramGoalItemParams
>(
  'DELETE_MENTORSHIP_PROGRAM_GOAL_ITEM',
  async ({ mentorshipProgramId, id, connectionId }) => {
    const response = await axios.delete(
      `/mentorship_programs/${mentorshipProgramId}/mentorship_program_enrollment_goals/${id}.json`,
      { data: { goal: { mentorshipProgramConnectionId: connectionId } } },
    );
    const isGoalItemDeleted = response.data.result;
    return isGoalItemDeleted;
  },
);

export const setMentorshipProgramGoalsTotalCount = createAction<{
  enrollmentId: number;
  totalCount: number;
}>('SET_MENTORSHIP_PROGRAM_GOALS_TOTAL_COUNT');
